import { useCallback, useEffect, useMemo } from 'react';

import { TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import useOKRInsightsPermissionsBasedOnEmployeeGoalsPrivileges from 'components/hooks/useOKRInsightsPermissionsBasedOnEmployeeGoalsPrivileges';
import { useParsedCohortQueryParam } from 'components/hooks/useParsedCohortQueryParam';
import { useSelector } from 'react-redux';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
import { selectMSTeamsContext } from 'selectors/msTeamsSelector';
import { checkOKRViewIsEnabled } from 'utils/helpers/featureFlags';

import { COHORT_TYPES_GOALS } from 'constants/ProjectConstants';
import { getEncodedCohortQueryParam, goToPage } from 'constants/helper';
import { sendPageViewEvent } from 'constants/mixpanelHelper';
import { routes } from 'constants/routes';

import { Tab, TabList } from 'uiToolkitV1/Tabs';

import { AgGridTreeView, DepartmentViewContainer } from '../AgGridTreeView';
import { useGoalsRouteMatch } from '../hooks/useGoalsRouteMatch';

// TODO: Remove this "not so good logic"
// Doing it in a hacky way for now, will refactor it later

const GoalsView = ({ showHierarchyToggle }) => {
  const selectedCohort = useSelectedView();

  if (checkOKRViewIsEnabled() && selectedCohort?.type === COHORT_TYPES_GOALS.MY_DEPARTMENT) {
    return <DepartmentViewContainer />;
  }

  return <AgGridTreeView showHierarchyToggle={showHierarchyToggle} />;
};

const GOALS_TABS = [
  {
    name: 'Explore',
    accessFn: () => true,
    route: (selectedCohort) => {
      if (
        [
          COHORT_TYPES_GOALS.MY_OKRs,
          COHORT_TYPES_GOALS.MY_TEAM,
          COHORT_TYPES_GOALS.MY_ORGANIZATION,
          COHORT_TYPES_GOALS.POD,
          COHORT_TYPES_GOALS.CUSTOM_TYPE,
        ].includes(selectedCohort?.type)
      ) {
        return [routes.goals.list, routes.goals.audit];
      }
      return routes.goals.audit;
    },
    Comp: GoalsView,
  },
  //   {
  //     name: (selectedCohort) => {
  //       if (![COHORT_TYPES_GOALS.ALL_EMPLOYEE].includes(selectedCohort?.type)) {
  //         return 'Hierarchy';
  //       }
  //       return 'Explore';
  //     },
  //     accessFn: () => true,
  //     route: routes.goals.audit,
  //     Comp: GoalsView
  //   },
  // {
  //   name: 'Card',
  //   accessFn: (selectedCohort, isMsTeamsVisitor) => {
  //     if (isMobileView || isMsTeamsVisitor) return false;

  //     return (
  //       [
  //         COHORT_TYPES_GOALS.MY_TEAM,
  //         COHORT_TYPES_GOALS.MY_ORGANIZATION,
  //         COHORT_TYPES_GOALS.POD,
  //         COHORT_TYPES_GOALS.CUSTOM_TYPE,
  //       ].includes(selectedCohort?.type) && selectedCohort?.moduleType !== 'GOAL'
  //     );
  //   },
  //   route: routes.goals.card,
  //   Comp: Card,
  // },
  // {
  //   name: 'Tree',
  //   accessFn: (selectedCohort, isMsTeamsVisitor) => {
  //     if (isMobileView || isMsTeamsVisitor) return false;

  //     return (
  //       [
  //         COHORT_TYPES_GOALS.MY_TEAM,
  //         COHORT_TYPES_GOALS.MY_ORGANIZATION,
  //         COHORT_TYPES_GOALS.POD,
  //         COHORT_TYPES_GOALS.CUSTOM_TYPE,
  //       ].includes(selectedCohort?.type) && selectedCohort?.moduleType !== 'GOAL'
  //     );
  //   },
  //   route: routes.goals.tree,
  //   Comp: GoalTree,
  // },
  // {
  //   name: 'Insights',
  //   accessFn: (selectedCohort, isMsTeamsVisitor, canViewInsights = true) => {
  //     if (!canViewInsights) return false;
  //     if (isMobileView || isMsTeamsVisitor) return false;
  //     if (!selectedCohort?.type) return false;
  //     if (selectedCohort?.type === COHORT_TYPES_GOALS.MY_DEPARTMENT) return false;

  //     // if (selectedCohort?.type === COHORT_TYPES_GOALS.ALL_EMPLOYEE) return checkUserIsGoalAdmin();

  //     return COHORT_TYPES_GOALS.MY_OKRs !== selectedCohort?.type;
  //   },
  //   route: routes.goals.insights,
  //   Comp: OKRInsights,
  // },
];

const StyledTabList = styled(TabList)`
  background-color: ${(props) =>
    // @ts-ignore
    props.theme.colors.surface.F5F7F9};
  ${(props) =>
    props.hideTabs &&
    css`
      display: none;
    `};
`;

const OKRHeaderTabsV2 = ({ hideTabs = false }) => {
  const selectedCohort = useSelectedView();
  const queryCohort = useParsedCohortQueryParam();

  const isMsTeamsVisitor = useSelector(selectMSTeamsContext);

  const history = useHistory();
  const location = useLocation();

  const { isAuditRoute, isListRoute, isCardRoute, isTreeRoute, isInsightsRoute } = useGoalsRouteMatch();

  /* Access Control for insights starts here */
  const { canViewDownloadIndividualInsights, canViewDownloadObjectiveInsights } =
    useOKRInsightsPermissionsBasedOnEmployeeGoalsPrivileges();

  const canViewInsights = canViewDownloadIndividualInsights || canViewDownloadObjectiveInsights;

  /* Access Control for insights ends here */

  const allowedGoalsTabs = useMemo(
    () =>
      GOALS_TABS.filter(({ name, accessFn }) =>
        /* TODO: change params for accessFn to named ones. */
        accessFn(selectedCohort || queryCohort, isMsTeamsVisitor, canViewInsights)
      ).map((tab) => tab),
    [selectedCohort, queryCohort, canViewInsights, isMsTeamsVisitor]
  );
  const allowedTabsRoutes = useMemo(
    () => allowedGoalsTabs.map(({ route }) => (typeof route === 'function' ? route(selectedCohort) : route)),
    [allowedGoalsTabs, selectedCohort]
  );

  const tabIndex = useMemo(() => {
    let matchedIndex = 0;
    allowedTabsRoutes.forEach((route, index) => {
      if (route instanceof Array) {
        route.forEach((r) => {
          if (location.pathname.includes(r)) {
            matchedIndex = index;
          }
        });
      } else if (location.pathname.includes(route)) {
        matchedIndex = index;
      }
    });
    return matchedIndex;
  }, [allowedTabsRoutes, location]);

  const onTabChangeHandler = useCallback(
    (index, keepOtherQueryParams) => {
      const matchedTab = allowedGoalsTabs[index];
      const { route } = matchedTab;
      const redirectTo = typeof route === 'function' ? route(selectedCohort) : route;
      const redirectToRoute = redirectTo instanceof Array ? redirectTo[0] : redirectTo;
      goToPage(
        history,
        redirectToRoute,
        getEncodedCohortQueryParam(selectedCohort, history.location, keepOtherQueryParams)
      );
    },
    [allowedGoalsTabs, history, selectedCohort]
  );

  const invalidRouteHandler = useCallback(() => {
    const route = allowedGoalsTabs?.[0]?.route;
    const redirectTo = typeof route === 'function' ? route(selectedCohort) : route;
    const redirectToRoute = redirectTo instanceof Array ? redirectTo[0] : redirectTo;

    history.replace({
      pathname: redirectToRoute,
      search: getEncodedCohortQueryParam(selectedCohort, history.location, true),
    });
  }, [allowedGoalsTabs, history, selectedCohort]);

  const invalidTabHandler = useCallback(() => {
    const hasValidTab = allowedTabsRoutes.some((route) => {
      if (route instanceof Array) {
        return route.some((r) => {
          const isMatched = matchPath(location.pathname, {
            path: r,
            exact: true,
          });
          return Boolean(isMatched);
        });
      }
      const isMatched = matchPath(location.pathname, {
        path: route,
        exact: true,
      });
      return Boolean(isMatched);
    });
    if (!hasValidTab) {
      onTabChangeHandler(0, true);
    }
  }, [allowedTabsRoutes, location, onTabChangeHandler]);

  useEffect(() => {
    if (selectedCohort) {
      sendPageViewEvent({
        eventProperties: {
          page: selectedCohort.name === 'My Goals' ? selectedCohort.name : `Goals ${selectedCohort.name}`,
          type:
            history.location.pathname.split('/')[2] === 'list' || history.location.pathname.split('/')[2] === 'audit'
              ? 'Explore'
              : history.location.pathname.split('/')[2],
          cohort: selectedCohort.name,
          source: 'TopBar',
        },
      });

      // if is on goals page no tab is selected
      if (!isAuditRoute && !isListRoute && !isInsightsRoute && !isCardRoute && !isTreeRoute) {
        invalidRouteHandler();
      } else {
        invalidTabHandler();
      }
    }
  }, [
    isAuditRoute,
    isListRoute,
    isInsightsRoute,
    isCardRoute,
    isTreeRoute,
    allowedGoalsTabs,
    selectedCohort,
    history,
    invalidRouteHandler,
    onTabChangeHandler,
    invalidTabHandler,
    location,
  ]);

  const areTabsHidden = hideTabs;

  const isOnlyOneTab = allowedGoalsTabs.length === 1;

  const showHierarchyToggle = allowedTabsRoutes.some((route) => {
    if (route instanceof Array) {
      return true;
    }
    return false;
  });

  return (
    <Tabs isLazy index={tabIndex} onChange={(index) => onTabChangeHandler(index, false)}>
      <StyledTabList hideTabs={areTabsHidden || isOnlyOneTab}>
        {allowedGoalsTabs.map(({ name }) => (
          // @ts-ignore
          <Tab key={name}>{name}</Tab>
        ))}
      </StyledTabList>
      <TabPanels>
        {allowedGoalsTabs.map(({ name, Comp }) => (
          <TabPanel key={name} p={0}>
            <Comp showHierarchyToggle={showHierarchyToggle} />
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
};

export default OKRHeaderTabsV2;
